import { ChatMessage as ChatMessageInterface, LangchainDocument, MessageType } from '@talon/web/lib/api';
import { useUserStore } from '@talon/web/src/context';
import { Accordion, AccordionPanel, Box, Tab, Tabs, Text } from 'grommet';
import { Blank, UserExpert, User as UserIcon } from 'grommet-icons';
import TalonLogo from 'jsx:@talon/web/public/images/talon-ai-logo.svg';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { parseIsoDateString } from 'src/utils/time';
import { TalonTip } from '../TalonTip';

export interface IChatMessageProps {
  message: Partial<ChatMessageInterface> & { localId?: string; status?: 'sending' | 'sent' | 'failed' | 'unconfirmed' };
  index: number;
  variant?: 'inbox';
  onRetry?: (localId: string) => void;
}

const MENTION_TOKEN_PATTERN = /@\[([^\]]+)\]\(([^)]+)\)/g;

const renderLineWithMentions = (line: string) => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  MENTION_TOKEN_PATTERN.lastIndex = 0;
  while ((match = MENTION_TOKEN_PATTERN.exec(line))) {
    if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
    parts.push(
      <Text key={`mention-${key++}`} weight="bold" color="brand">
        @{match[1]}
      </Text>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < line.length) parts.push(line.slice(lastIndex));
  return parts;
};

export const ChatMessage = ({ message, index, variant, onRetry }: IChatMessageProps) => {
  useEffect(() => {
    setMyMsg(message.user_id === userStore.user.id);

    switch (message.type) {
      case MessageType.USER_MESSAGE:
        setBg('light-1');
        setIsSystemMsg(false);
        setIcon(
          <TalonTip
            content={
              <Text>
                {userStore.user.first_name} {userStore.user.last_name}
              </Text>
            }
          >
            <UserIcon />
          </TalonTip>
        );
        break;
      case MessageType.AI_MESSAGE:
        setBg('white');
        setIcon(<TalonLogo />);
        setIsSystemMsg(false);
        break;
      case MessageType.SUPPORT_MESSAGE:
        setBg('accent-2');
        setIcon(
          <TalonTip content={<Text>BA Expert</Text>}>
            <UserExpert />
          </TalonTip>
        );
        setIsSystemMsg(false);
        break;
      case MessageType.SYSTEM_MESSAGE:
        setIsSystemMsg(true);
        setBg('white');
        setIcon(<Blank />);
        break;
      default:
        console.log('Unknown message type:', message.type);
        setBg('white');
        break;
    }
  }, [message]);
  const userStore = useUserStore();

  const [bg, setBg] = useState('');
  const [icon, setIcon] = useState(
    <TalonTip
      content={
        <Text>
          {userStore.user.first_name} {userStore.user.last_name}
        </Text>
      }
    >
      <UserIcon />
    </TalonTip>
  );
  const [myMsg, setMyMsg] = useState(message.user_id === userStore.user.id);
  const [isSystemMsg, setIsSystemMsg] = useState(false);

  if (variant === 'inbox') {
    const staff = message.type === MessageType.SUPPORT_MESSAGE;
    return (
      <Box direction="row" justify={staff ? 'end' : 'start'} margin="small">
        <Box
          background={staff ? 'brand-tint' : 'white'}
          border={{ color: '#E3E3E3' }}
          round="8px"
          pad="small"
          width={{ max: '75%' }}
        >
          <Text size="14px" style={{ whiteSpace: 'pre-wrap' }}>
            {message.text}
          </Text>
          {message.status === 'sending' && (
            <Text size="12px" color="#777">
              Sending…
            </Text>
          )}
          {message.status === 'failed' && (
            <Text size="12px" color="#c62828" weight={600}>
              Not sent · <button onClick={() => message.localId && onRetry?.(message.localId)}>Retry</button>
            </Text>
          )}
          {message.status === 'unconfirmed' && (
            <Text size="12px" color="#7a4a00">
              Delivery unconfirmed · Refresh to check before sending again
            </Text>
          )}
          <Box align="start" justify="start">
            <Text size="xsmall" color="light-5">
              {message.created_at && parseIsoDateString(message.created_at).time}
            </Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return isSystemMsg ? (
    <Box key={message.id} margin="small" align="center" justify="center">
      <Text size="xsmall" color="light-5">
        {message.text}
      </Text>
    </Box>
  ) : (
    <Box key={message.id} direction={myMsg ? 'row-reverse' : 'row'} flex={false} margin="small" align="end">
      <Box align="center" justify="end" margin="xsmall" height="40px" width="40px">
        {icon}
      </Box>
      <Box
        direction="column"
        background={bg}
        flex="grow"
        border={{ color: '#d9d9e3' }}
        round="small"
        width={{ max: '90%' }}
      >
        <Box pad="small" overflow="wrap">
          {message.type === MessageType.AI_MESSAGE ? (
            <Box style={{ lineHeight: 1.5 }}>
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                }}
              >
                {message.text ?? ''}
              </ReactMarkdown>
            </Box>
          ) : (
            <Text>
              {message.text?.split('\n').map((line, index) => (
                <Box key={`${line}-${index}`}>
                  {renderLineWithMentions(line)}
                  <br />
                </Box>
              ))}
            </Text>
          )}
          <Box align="start" justify="start">
            <Text size="xsmall" color="light-5">
              {message.created_at && parseIsoDateString(message.created_at).time}
            </Text>
          </Box>
        </Box>
        {!!message.sources?.length && (
          <Accordion key={`sourceDocsAccordion-${index}`}>
            <AccordionPanel label="Sources">
              <Tabs alignControls="start">
                {message.sources.map((doc: LangchainDocument, docIndex) => (
                  <Tab title={docIndex + 1} key={docIndex}>
                    <Box direction="column" pad="small" gap="small">
                      <Text size="small">
                        (Excerpt from{' '}
                        <a href={doc.metadata.url} target="_blank" rel="noreferrer">
                          {doc.metadata.source}
                        </a>
                        )
                      </Text>
                      {doc.pageContent && (
                        <Box pad="small">
                          {doc.pageContent.split('\n').map((line, i) => (
                            <Text key={i}>
                              {line}
                              <br />
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Tab>
                ))}
              </Tabs>
            </AccordionPanel>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};
